import { router } from '@inertiajs/core';
import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import omit from 'lodash/omit';
import toInteger from 'lodash/toInteger';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { ArrowRightCircle, Calendar, Check, ChevronRight, Heart, Image, Share, UserPlus, Users, Zap } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';

import { dispatchNewCampaignEvent, dispatchNewDonationEvent } from '../../Shared/eventTracking';
import { useLocale } from '../../Shared/locale';
import { Modal } from '../../Shared/Modal/Modal';
import { Campaign, CampaignType, Charity, Donation, Event, PaymentStatus, Product, Project, ProjectType, TeamMember, User } from '../../Shared/types';
import { Card } from '../../Shared/UI/Card';
import DateRange from '../../Shared/UI/DateRange';
import { Icon } from '../../Shared/UI/Icon';
import InputGroup from '../../Shared/UI/InputGroup';
import { ProgressBar } from '../../Shared/UI/ProgressBar';
import { SearchFilterInput } from '../../Shared/UI/SearchFilterInput';
import SupportaSymbol from '../../Shared/UI/SupportaSymbol';
import { useCharityStyle } from '../../Shared/useCharityStyle';
import { useGaEvents } from '../../Shared/useGaEvents';
import EventCard from '../Event/EventCard';
import { useRouter } from '../router';
import { SharedProps } from '../types';
import { AButton, Button, LinkButton } from '../UI/Button';
import CharityLayout from '../UI/CharityLayout';
import Container from '../UI/Container';
import { EmbedContext } from '../UI/EmbedProvider';
import { Footer } from '../UI/Footer';
import Header from '../UI/Header';
import { NavBar } from '../UI/NavBar';
import PageOfflineWarning from '../UI/PageOfflineWarning';
import CampaignCard from './CampaignCard';
import DonationBoxQrCode from './DonationBoxQrCode';
import JoinTeamForm from './JoinTeamForm';
import { OnboardingSteps } from './OnboardingSteps';
import { ProjectLabel } from './ProjectLabel';
import ShareOptions from './ShareOptions';
import { UserNav } from './UserNav';

const MEMBERS_PER_PAGE = 5;
const DONATIONS_PER_PAGE = 10;

export interface CampaignPageProps extends SharedProps {
  campaign: Campaign & {
    project: Project;
    event: Event | null;
    active_products: Product[];
    optional_products: Product[];
    team: Campaign | null;
    members_count: number;
    allow_join: boolean;
    donations: Donation[];
    donations_count: number;
    user: User | null;
    document_title: string;
  };
  newCampaign: boolean;
  onboarding: boolean;
  donation?: Donation & {
    payment_status: PaymentStatus;
  };
  teamMembers: TeamMember[];
  teamMembersCount: number;
  potentialTeamMembers?: (Campaign & {
    project: Project;
    event: Event | null;
  })[];
  charity: Charity & {
    funds_raised: number;
    campaigns_count: number;
  };
}

export default function CampaignPage({
  campaign,
  newCampaign,
  onboarding,
  donation,
  teamMembers,
  teamMembersCount,
  potentialTeamMembers = [],
  auth: { user },
  charity,
  params,
}: CampaignPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { formatCurrency, formatRelativeDate, formatDate, formatNumber } = useLocale();
  const { isEmbedded } = useContext(EmbedContext);

  const activeProducts = campaign.active_products.filter((product) => !product.required);

  const membersLimit = Math.max(toInteger(params.members_limit), MEMBERS_PER_PAGE);
  const [membersLoading, setMembersLoading] = useState(false);
  const hasMoreMembers = teamMembers.length < teamMembersCount;

  const donationsLimit = Math.max(toInteger(params.donations_limit), DONATIONS_PER_PAGE);
  const [donationsLoading, setDonationsLoading] = useState(false);
  const hasMoreDonations = campaign.donations.length < campaign.donations_count;

  const loadMoreMembers = () => {
    setMembersLoading(true);
    const newLimit = Math.max(membersLimit + MEMBERS_PER_PAGE, MEMBERS_PER_PAGE);
    const url = routes.campaign_page(campaign.id);

    router.get(
      url,
      {
        ...params,
        members_limit: newLimit,
        members_search: typeof params.members_search === 'string' ? params.members_search : undefined,
      },
      {
        only: ['teamMembers', 'teamMembersCount', 'params'],
        preserveScroll: true,
        preserveState: true,
        replace: true,
        onSuccess: () => {
          setMembersLoading(false);
        },
      }
    );
  };

  /**
   * Use "infinite" pagination style to append new donations to the list.
   */
  const loadMoreDonations = () => {
    setDonationsLoading(true);
    const newLimit = Math.max(donationsLimit + DONATIONS_PER_PAGE, DONATIONS_PER_PAGE);
    const url = routes.campaign_page(campaign.id);

    // Visit the next page of donations
    router.get(
      url,
      {
        ...params,
        donations_limit: newLimit,
      },
      {
        only: ['campaign', 'params'],
        preserveScroll: true,
        preserveState: true,
        replace: true,
        onSuccess: () => {
          setDonationsLoading(false);
        },
      }
    );
  };

  /**
   * Keep these params hidden from the URL to avoid them being shared.
   */
  useEffect(() => {
    if (params.members_search || params.members_limit || params.donations_limit) {
      const url = new URL(window.location.href);
      url.searchParams.delete('members_search');
      url.searchParams.delete('members_limit');
      url.searchParams.delete('donations_limit');
      history.replaceState({}, '', url);
    }
  }, [params.members_search, params.members_limit, params.donations_limit]);

  useCharityStyle(charity);

  const { project, event } = campaign;
  const imageUrl = campaign.image_url || project.image_url || charity.image_url;

  const isLoggedInAsOwner = user && user.id === campaign.user?.id;

  const fundraisingTarget = campaign.fundraising_target || 0;
  const hasFundraisingTarget = fundraisingTarget > 0;

  // Instantly open the share modal if the param is present in the URL.
  const [showShareModal, setShowShareModal] = useState('share' in params);
  const [showJoinTeamModal, setShowJoinTeamModal] = useState('join_team' in params);

  // Removes the share/join_team param from the URL after initially opening the share modal.
  useEffect(() => {
    if ('share' in params || 'join_team' in params) {
      // Instantly removing the search param doesn't work, hence the timeout.
      const timer = window.setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('share');
        url.searchParams.delete('join_team');

        window.history.replaceState({}, '', url.toString());
      }, 500);

      return () => window.clearTimeout(timer);
    }
  }, [params]);

  useEffect(() => {
    if (user && potentialTeamMembers.length === 0 && showJoinTeamModal) {
      // If the user is logged in and there are no potential team members, redirect to the create campaign page.
      router.visit(routes.create_campaign_page(charity.id, project.type, {
        projectId: event ? null : project.id,
        eventId: event?.id,
        teamId: campaign.id,
      }));
    }
  }, [user, potentialTeamMembers, showJoinTeamModal]);

  // Google Analytics tracking for new donations and new campaigns.
  useGaEvents(charity.google_analytics_id);
  useGaEvents(window.Supporta?.googleAnalyticsId);

  // Dispatch event when the campaign was just created.
  useEffect(() => {
    if (newCampaign) {
      dispatchNewCampaignEvent({
        charity,
        project: project,
        campaign,
      });
    }
  }, [newCampaign]);

  // Dispatch event when a donation is accepted.
  useEffect(() => {
    if (donation?.active || donation?.payment_status === PaymentStatus.PENDING) {
      dispatchNewDonationEvent({
        charity,
        project: project,
        campaign,
        donation: {
          ...donation,
          frequency: 'oneoff',
        },
      });
    }
  }, [donation]);

  const showDonations = project.type !== ProjectType.DonationBox;
  const showQrCode = project.type === ProjectType.DonationBox;

  const startDate = campaign.start_date || event?.start_date || campaign.team?.start_date;
  const endDate = campaign.end_date || event?.end_date || campaign.team?.end_date;
  const campaignDate = startDate || endDate;

  const showStartCampaignButton = project.default;

  return (
    <>
      <NavBar charity={charity} user={user} showCampaignButton={showStartCampaignButton} />

      {isLoggedInAsOwner && (
        <UserNav campaign={campaign} user={user} />
      )}

      <Header className={classNames(campaign.fundraising_target && campaign.fundraising_target > 0 && '!border-none')}>
        <Container>
          {onboarding && (
            <OnboardingSteps campaign={campaign} charity={charity} isLoggedInAsOwner={!!isLoggedInAsOwner} />
          )}

          {(!campaign.enabled || !project.enabled) && (
            <PageOfflineWarning />
          )}

          {donation && (
            <>
              {donation.active && (
                <Card shadow={false} className="bg-white border-2 border-emerald-500/30">
                  <h4 className="flex items-center gap-4 mb-4 text-base font-bold text-emerald-600">
                    <span className="flex items-center justify-center flex-shrink-0 w-5 h-5 text-white rounded-full bg-emerald-600">
                      <Check strokeWidth={3} className="w-3 h-3" />
                    </span>
                    {campaign.donation_message ? t('frontend:message_from_campaigner', { campaigner: campaign.campaigner }) : t('frontend:donation_success')}
                  </h4>
                  <div className="text-lg whitespace-pre-line">
                    {campaign.donation_message || project.donation_message || charity.default_donation_message}
                  </div>
                </Card>
              )}
              {!donation.active && donation.payment_status === PaymentStatus.PENDING && (
                <Card shadow={false} className="font-medium bg-white border-2 border-amber-500">
                  {t('frontend:donation_pending')}
                </Card>
              )}
              {!donation.active && donation.payment_status !== PaymentStatus.PENDING && (
                <Card shadow={false} className="font-medium bg-white border-2 border-red-500">
                  {t('frontend:donation_failed')}
                </Card>
              )}
            </>
          )}

          <div>
            {campaign.type === CampaignType.Individual && campaign.team && (
              <Link
                href={routes.campaign_page(campaign.team.id)}
                className="flex items-center mb-1 space-x-2 font-medium text-slate-600 hover:text-slate-900"
              >
                <div className="flex items-center justify-center w-6 h-6 my-1 text-xs rounded-full bg-project text-project-comp shrink-0">
                  <Icon>
                    <Users />
                  </Icon>
                </div>
                <span>
                  {campaign.team.title}
                </span>
                <Icon>
                  <ChevronRight />
                </Icon>
              </Link>
            )}

            <div>
              <h1 className="flex mb-1 sm:mb-2 space-x-2 text-2xl sm:text-3xl font-extrabold text-slate-800">
                {campaign.type === CampaignType.Team && (
                  <div className="mr-1 rounded-full w-6 h-6 sm:w-8 sm:h-8 bg-project text-project-comp !text-md flex items-center justify-center my-1 shrink-0">
                    <Icon className="!text-xs sm:!text-base">
                      <Users />
                    </Icon>
                  </div>
                )}
                <div>
                  {campaign.title}
                </div>
              </h1>

              <div className="flex items-center text-slate-500 flex-wrap -mb-1">
                {campaignDate && (
                  <span className="bg-slate-800/5 text-slate-800 px-2 py-1 text-xs sm:text-sm leading-tight rounded-full inline-block overflow-hidden whitespace-nowrap text-ellipsis mr-1 my-1">
                    <Icon className="mr-1">
                      <Calendar />
                    </Icon>
                    <DateRange start={campaignDate} end={endDate || null} />
                  </span>
                )}

                <ProjectLabel project={project} event={event} className="my-1" />
              </div>
            </div>
          </div>

          <div className="relative flex flex-col items-center justify-center overflow-hidden bg-white rounded-2xl aspect-video">
            {imageUrl && (
              <img src={imageUrl} alt={campaign.title} className="object-cover w-full h-full" />
            )}
            {!imageUrl && (
              <div className="absolute inset-0 bg-gradient-to-tl from-project/15 to-project/10 z-[3]" />
            )}
            {!imageUrl && (
              <Icon className="text-6xl text-black opacity-10 z-[2]">
                <Image strokeWidth={1} />
              </Icon>
            )}
          </div>

          <div className="flex justify-between items-center">
            <div className="mr-3">
              <div className="text-slate-900">
                <strong className="text-xl font-bold">
                  {formatCurrency(campaign.funds_raised)}
                </strong>
                {hasFundraisingTarget && (
                  <span className="text-lg font-normal">
                    {' / '}
                    {formatCurrency(fundraisingTarget)}
                  </span>
                )}
              </div>
              {t('frontend:raised_for_charity', { charity: charity.title })}
            </div>

            {charity.logo_url && (
              <a
                href={!charity.public && charity.website ? charity.website : routes.home_page(charity.slug, event?.slug)}
              >
                <img src={charity.logo_url} className="max-h-10 max-w-16 w-full" alt={charity.title} />
              </a>
            )}
          </div>
        </Container>
      </Header>

      {campaign.fundraising_target !== null && campaign.fundraising_target > 0 && (
        <ProgressBar
          current={campaign.funds_raised}
          target={campaign.fundraising_target}
          brandColor={charity.brand_color}
          rounded={false}
          className="-mt-1"
        />
      )}

      <Container className="!pb-12 sm:!pb-14">
        {!project.ended && (
          <div className="space-y-6">
            <div className={classNames('grid grid-cols-[1fr_1fr] gap-2')}>
              <LinkButton
                href={routes.donation_page(campaign.id)}
                size="lg"
                className="!font-bold"
              >
                <Icon className="mr-2">
                  <Heart />
                </Icon>
                {t('frontend:donate')}
              </LinkButton>
              <Button
                onClick={() => setShowShareModal(true)}
                size="lg"
                variant="secondary"
              >
                <Icon className="mr-2">
                  <Share />
                </Icon>
                {t('frontend:share')}
              </Button>
              <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} size="sm">
                <ShareOptions campaign={campaign} charity={charity} />
              </Modal>
            </div>
            {activeProducts.length > 0 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-slate-500">
                    {t('frontend:order_now')}
                  </h4>
                  {activeProducts.map((product) => (
                    <Card
                      link={product.max_per_order > 0 ? {
                        href: `${routes.donation_page(campaign.id)}?product=${product.id}`,
                        ariaLabel: product.title,
                      } : undefined}
                      spacing="xs"
                      className="!py-3"
                      border
                      key={product.id}
                    >
                      <div className={classNames(
                        'grid items-center gap-2',
                        product.image_url ? 'grid-cols-[30px_1fr_fit-content(100px)_16px]' : 'grid-cols-[1fr_fit-content(100px)_16px]',
                        product.max_per_order === 0 && 'opacity-60',
                      )}>
                        {product.image_url && (
                          <div className="w-[30px] h-[30px]">
                            <img src={product.image_url} alt={product.title} className="block object-cover w-full h-full rounded" />
                          </div>
                        )}
                        <div>
                          <strong>
                            {product.title}
                          </strong>
                        </div>
                        <div className="text-right">
                          {product.max_per_order > 0 ? formatCurrency(product.selling_price) : (
                            <div className="px-1 text-sm rounded-sm bg-slate-100">
                              {t('frontend:sold_out')}
                            </div>
                          )}
                        </div>
                        <div>
                          <Icon>
                            <ChevronRight />
                          </Icon>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-6">
          <div className="space-y-1 text-slate-500 text-sm">
            <div className="flex space-x-3">
              <div>
                <Icon>
                  <UserPlus />
                </Icon>
              </div>
              <div>
                {t('frontend:page_created_by_name_on_date', {
                  name: campaign.campaigner,
                  date: formatDate(campaign.created_at),
                })}
              </div>
            </div>
          </div>

          <div className="text-lg whitespace-pre-line">
            {campaign.description || (campaign.type === CampaignType.Individual
              ? (project.default_individual_description || charity.default_individual_description)
              : (project.default_team_description || charity.default_team_description))}
          </div>
        </div>

        {campaign.team && (
          <>
            <hr className="border-slate-150" />

            <div className="space-y-4">
              <div className="flex justify-between">
                <h2 className="text-xl font-bold">
                  {t('frontend:team')}
                </h2>
              </div>

              <CampaignCard
                campaign={{
                  ...campaign.team,
                  project: project,
                  event: event,
                }}
                charity={charity}
              />
            </div>
          </>
        )}

        {campaign.type === CampaignType.Team && (
          <>
            <hr className="border-slate-150" />

            <div className="space-y-4">
              <div className="flex justify-between">
                <h2 className="flex items-center text-xl font-bold">
                  {t('frontend:team_members')}
                  {' '}
                  <span className="flex items-center justify-center h-6 ml-2 text-sm font-normal rounded-full min-w-[1.5rem] px-1 bg-slate-200 opacity-60">
                    {formatNumber(campaign.members_count)}
                  </span>
                </h2>
                {campaign.allow_join && !project.ended && (
                  <>
                    {user && potentialTeamMembers.length === 0 && (
                      <LinkButton
                        href={routes.create_campaign_page(charity.id, project.type, {
                          projectId: event ? null : project.id,
                          eventId: event?.id,
                          teamId: campaign.id,
                        })}
                        size="sm"
                        variant="secondary"
                      >
                        <Icon className="mr-2">
                          <UserPlus />
                        </Icon>
                        {t('frontend:join')}
                      </LinkButton>
                    )}
                    {(!user || potentialTeamMembers.length > 0) && (
                      <>
                        <Button
                          onClick={() => setShowJoinTeamModal(true)}
                          size="sm"
                          variant="secondary"
                        >
                          <Icon className="mr-2">
                            <UserPlus />
                          </Icon>
                          {t('frontend:join')}
                        </Button>
                        <Modal isOpen={showJoinTeamModal} onClose={() => setShowJoinTeamModal(false)} size="sm">
                          <JoinTeamForm
                            charity={charity}
                            team={campaign}
                            showLogin={!user}
                            potentialTeamMembers={potentialTeamMembers}
                            onSuccess={() => setShowJoinTeamModal(false)}
                          />
                        </Modal>
                      </>
                    )}
                  </>
                )}
              </div>

              {campaign.members_count > MEMBERS_PER_PAGE && (
                <InputGroup>
                  <SearchFilterInput
                    name="members_search"
                    aria-label={t('shared:filter.search')}
                    payload={() => ({ ...omit(params, 'members_search'), members_limit: undefined })}
                  />
                </InputGroup>
              )}

              {teamMembers.length === 0 && !membersLoading && (
                <div className="text-slate-500">
                  {campaign.members_count === 0 ? t('frontend:no_team_members_yet') : t('frontend:no_results')}
                </div>
              )}

              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  {teamMembers.map((member) => (
                    <CampaignCard
                      campaign={{
                        ...member,
                        project: project,
                        event: event,
                      }}
                      charity={charity}
                      key={member.id}
                    />
                  ))}
                </div>
              )}

              {hasMoreMembers && (
                <Button
                  onClick={() => loadMoreMembers()}
                  variant="secondary"
                  className="block w-full !rounded-lg"
                  loading={membersLoading}
                  disabled={membersLoading}
                >
                  {t('frontend:show_more_team_members')}
                </Button>
              )}
            </div>
          </>
        )}

        {event && (
          <>
            <hr />
            <div className="space-y-4">
              <h2 className="text-xl">
                {t('frontend:event')}
              </h2>
              <EventCard event={event} />
            </div>
          </>
        )}

        {showDonations && (
          <>
            <hr className="border-slate-150" />

            <div className="space-y-4">
              <div>
                <h2 className="flex items-center text-xl font-bold">
                  {t('frontend:donations')}
                  {' '}
                  <span className="flex items-center justify-center h-6 ml-2 text-sm font-normal rounded-full min-w-[1.5rem] px-1 bg-slate-200 opacity-60">
                    {formatNumber(campaign.donations_count)}
                  </span>
                </h2>
                {campaign.donations.length === 0 && (
                  <div className="mt-2 text-slate-500">
                    {t('frontend:no_donations_yet')}
                  </div>
                )}
              </div>

              {campaign.donations.length > 0 && (
                <div className="space-y-2">
                  {campaign.donations.map((donation) => (
                    <div className="p-4 rounded-lg sm:p-6 bg-slate-50" key={donation.id}>
                      <div className="space-y-2">
                        {donation.amount > 0 && (
                          <div className="text-lg font-bold">
                            {formatCurrency(donation.amount)}
                          </div>
                        )}

                        {donation.message && (
                          <div className="whitespace-pre-line text-slate-600">
                            {donation.message}
                          </div>
                        )}

                        <div className="flex items-end justify-between">
                          <span className="font-medium">
                            {donation.published_name || t('frontend:anonymous')}
                          </span>
                          <small className="text-slate-400 whitespace-nowrap">
                            {formatRelativeDate(donation.created_at)}
                          </small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hasMoreDonations && (
                <Button
                  onClick={() => loadMoreDonations()}
                  variant="secondary"
                  className="block w-full !rounded-lg"
                  loading={donationsLoading}
                  disabled={donationsLoading}
                >
                  {t('frontend:show_more_donations')}
                </Button>
              )}
            </div>
          </>
        )}

        {showQrCode && (
          <DonationBoxQrCode campaign={campaign} />
        )}
      </Container>

      {charity.public && showStartCampaignButton && !isLoggedInAsOwner && !isEmbedded && (
        <div className="bg-gradient-to-b from-project/10 relative">
          <Container spacing="lg" className="relative z-1">
            <div>
              {charity.logo_url && (
                <div className="bg-white shadow-sm rounded-xl p-2 sm:p-3 flex justify-center items-center shrink-0 w-24 h-16 mb-6">
                  <img src={charity.logo_url} className="object-contain max-w-full max-h-full" alt={charity.title} />
                </div>
              )}

              <div>
                <h2 className="text-xl mb-2">
                  {t('frontend:make_impact_for_charity', { charity: charity.title })}
                </h2>

                {charity.description && (
                  <p className="mb-4">{charity.description}</p>
                )}

                <ul className="space-y-2 mb-6">
                  <li>
                    <Icon className="text-project mr-3">
                      <Heart />
                    </Icon>
                    <Trans
                      i18nKey="frontend:n_campaigns"
                      values={{
                        value: formatNumber(charity.campaigns_count),
                        count: charity.campaigns_count,
                      }}>
                      <strong />
                    </Trans>
                  </li>
                  <li>
                    <Icon className="text-project mr-3">
                      <Zap />
                    </Icon>
                    <Trans
                      i18nKey="frontend:x_raised"
                      values={{
                        value: formatCurrency(charity.funds_raised, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
                      }}
                    >
                      <strong />
                    </Trans>
                  </li>
                  <li>
                    <Icon className="text-project mr-3">
                      <ArrowRightCircle />
                    </Icon>
                    <Trans i18nKey="frontend:100%_to_charity" values= {{ charity: charity.title }}>
                      <strong />
                    </Trans>
                  </li>
                </ul>

                <p className="flex flex-wrap space-y-2 sm:space-y-0 sm:space-x-2">
                  <LinkButton href={routes.create_campaign_page(charity.id)}>
                    <span className="flex items-center justify-center">
                      <SupportaSymbol className="w-3.5 h-3.5 mr-2" />
                      {t('frontend:start_campaign')}
                    </span>
                  </LinkButton>
                  <AButton
                    href={routes.home_page(charity.id)}
                    variant="secondary"
                    target={isEmbedded ? '_blank' : undefined}
                    rel={isEmbedded ? 'noopener noreferrer' : undefined}
                  >
                    {t('frontend:view_all_campaigns')}
                  </AButton>
                </p>
              </div>
            </div>
          </Container>
        </div>
      )}
      <Footer charity={charity} />
    </>
  );
}

CampaignPage.layout = (page: ReactNode) => <CharityLayout>{page}</CharityLayout>;
